/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
import { useQuery } from "@apollo/client";
import AmericanCompanyContext from "components/company/AmericanCompanyContext";
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import ExecutivesOrDirectorSheetContent from "components/company/sections/collapsible/ExecutivesOrDirectorSheetContent";
import type { TExecutiveOrDirector } from "components/company/sections/collapsible/types";
import ContentSectionBreadcrumbs from "components/company/sections/ContentSectionBreadcrumbs";
import Avatar from "components/core/Avatar";
import { Sheet } from "components/ui/Sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs/v2/TabsV2";
import formatMoneyValue from "components/utils/formatMoneyValue";
import { getYear, parseISO } from "date-fns";
import type { IExecutiveCompensationHistoryItemFieldsFragment } from "lib/graphql/fragments/__generated__/ExecutiveCompensationHistoryItemFields.generated";
import ExecutivesAndDirectorsQuery from "lib/graphql/queries/ExecutivesAndDirectorsQuery";
import type {
  IExecutivesAndDirectorsQuery,
  IExecutivesAndDirectorsQueryVariables,
} from "lib/graphql/queries/__generated__/ExecutivesAndDirectorsQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import { ArrowUpIcon, ChevronRightIcon } from "lucide-react";
import { useContext, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { ISecurityCountry } from "types/graphqlTypes";

interface IExecutivesAndDirectorsProps {
  isExpanded: boolean;
  tickerSymbol: string;
  onToggleCollapsed: () => void;
}

export default function ExecutivesAndDirectors({
  tickerSymbol,
  isExpanded,
  onToggleCollapsed,
}: IExecutivesAndDirectorsProps) {
  const selfie = useSelfie();
  const { data, loading } = useQuery<IExecutivesAndDirectorsQuery, IExecutivesAndDirectorsQueryVariables>(
    ExecutivesAndDirectorsQuery,
    {
      variables: {
        tickerSymbol,
        country: ISecurityCountry.Us,
      },
    },
  );

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  if (!selfie) {
    return null;
  }

  if (!selfie.isPremium) {
    return null;
  }

  if (
    data &&
    data.issuerByTickerSymbol?.__typename === "AmericanCompanyIssuer" &&
    data.issuerByTickerSymbol.executivesAndDirectors
  ) {
    const { executivesAndDirectors } = data.issuerByTickerSymbol;

    return (
      <CollapsibleContentSection
        title="Executives and Directors"
        isExpanded={isExpanded}
        isNewSection={parseISO("2023-07-01")}
        onToggleCollapsed={onToggleCollapsed}
        primaryColor={palette.mariner}
        renderSubtitle={() => (
          <ContentSectionBreadcrumbs segments={["Governance", "Executives and Directors Insight"]} />
        )}
        renderContentSummary={undefined}
        renderContent={() => {
          return (
            <div className="rounded-md bg-white p-1 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-200">
              <Tabs className="w-full bg-white p-4 font-aeonik font-normal" defaultValue="OFFICERS">
                <TabsList>
                  <TabsTrigger value="OFFICERS">Officers</TabsTrigger>
                  <TabsTrigger value="DIRECTORS">Directors</TabsTrigger>
                </TabsList>

                <TabsContent className="-mx-2 border-none p-0" value="OFFICERS">
                  <ExecutivesAndDirectorsList
                    executivesAndDirectors={executivesAndDirectors.filter((person) => person.isOfficer)}
                  />
                </TabsContent>

                <TabsContent className="-mx-2 border-none p-0" value="DIRECTORS">
                  <ExecutivesAndDirectorsList
                    executivesAndDirectors={executivesAndDirectors.filter(
                      (person) => person.isDirector && !person.isOfficer,
                    )}
                  />
                </TabsContent>
              </Tabs>
            </div>
          );
        }}
      />
    );
  }

  return null;
}

interface IExecutivesAndDirectorsListProps {
  executivesAndDirectors: TExecutiveOrDirector[];
}
function ExecutivesAndDirectorsList({ executivesAndDirectors }: IExecutivesAndDirectorsListProps) {
  const [activeExecutiveOrDirector, setActiveExecutiveOrDirector] = useState<TExecutiveOrDirector | null>(null);
  const issuer = useContext(AmericanCompanyContext);
  // Shouldn't happen but /shrug
  if (!issuer) {
    return null;
  }

  return (
    <>
      <ul className="divide-y divide-gray-100">
        {executivesAndDirectors.map((person) => {
          const executiveCompensationHistoryInUSD = [...person.executiveCompensationHistoryInUSD].sort((a, b) => {
            const aDate = parseISO(a.reportDate);
            const bDate = parseISO(b.reportDate);
            return aDate > bDate ? -1 : 1;
          });
          const latestExecutiveCompensation =
            executiveCompensationHistoryInUSD.length > 0 ? executiveCompensationHistoryInUSD[0] : null;
          const previousExecutiveCompensation =
            executiveCompensationHistoryInUSD.length > 1 ? executiveCompensationHistoryInUSD[1] : null;

          return (
            <li key={person.id} className="flex items-center justify-between gap-x-6 px-4 py-5">
              <div className="flex gap-x-4">
                <Avatar
                  src={null}
                  hashValue={person.id}
                  alt={`${person.firstName} ${person.lastName}`}
                  size={40}
                  squircle={true}
                />

                <div className="min-w-0 flex-auto space-y-1">
                  <p className="text-sm font-semibold leading-6 text-gray-900">{person.fullName}</p>
                  <p className="w-48 truncate text-xs font-medium tracking-wider text-slate-500">
                    {person.standardTitle}
                  </p>
                  {person.age && (
                    <small className="text-xs font-medium italic text-slate-400">{person.age} years old</small>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-x-4">
                <div className="hidden sm:flex sm:flex-col sm:items-end">
                  {latestExecutiveCompensation && (
                    <ExecutiveCompensationHighlight
                      executiveCompensation={latestExecutiveCompensation}
                      previousExecutiveCompensation={previousExecutiveCompensation}
                    />
                  )}
                </div>
                {person.executiveCompensationHistoryInUSD.length > 0 && (
                  <button type="button" onClick={() => setActiveExecutiveOrDirector(person)}>
                    <ChevronRightIcon
                      className="h-6 w-6 flex-none text-gray-400 hover:text-gray-600"
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <Sheet onOpenChange={() => setActiveExecutiveOrDirector(null)} open={activeExecutiveOrDirector !== null}>
        {activeExecutiveOrDirector && (
          <ExecutivesOrDirectorSheetContent issuer={issuer} person={activeExecutiveOrDirector} />
        )}
      </Sheet>
    </>
  );
}

interface IExecutiveCompensationHighlightProps {
  executiveCompensation: IExecutiveCompensationHistoryItemFieldsFragment;
  previousExecutiveCompensation?: IExecutiveCompensationHistoryItemFieldsFragment | null;
}

function ExecutiveCompensationHighlight({
  executiveCompensation,
  previousExecutiveCompensation,
}: IExecutiveCompensationHighlightProps) {
  const totalCompensation = executiveCompensation.totalCompensation || executiveCompensation.calculatedTotal;
  const dollarValue = formatMoneyValue(totalCompensation, "short", "en-US");

  const hadIncrease = previousExecutiveCompensation
    ? totalCompensation >
      (previousExecutiveCompensation.totalCompensation || previousExecutiveCompensation.calculatedTotal)
    : false;
  const increaseInPercent = previousExecutiveCompensation
    ? (totalCompensation /
        (previousExecutiveCompensation.totalCompensation || previousExecutiveCompensation.calculatedTotal)) *
        100 -
      100
    : 0;
  const formattedIncreaseInPercent = `${Math.abs(increaseInPercent).toFixed(1)}%`;
  return (
    <>
      <div className="flex items-center space-x-2">
        <strong className="text-sm font-bold text-emerald-700">${dollarValue}</strong>
        {hadIncrease && (
          <span className="flex items-center rounded-md bg-emerald-50 px-1.5 py-1 text-xs font-bold text-emerald-700">
            <ArrowUpIcon className="h-4 w-4" />
            {formattedIncreaseInPercent}
          </span>
        )}
      </div>

      <small className="mt-1 text-xs italic tracking-wide text-gray-600">
        Total Compensation, {getYear(parseISO(executiveCompensation.reportDate))}
      </small>
    </>
  );
}
